/**
 * 十神计算与颜色工具
 * - 颜色按来源天干的五行取色
 * - “正/偏”区分深浅：正色更深、偏色更浅
 */

// 判断天干阴阳
export const isYangGan = (gan) => {
  const yangGan = ['甲', '丙', '戊', '庚', '壬'];
  return yangGan.includes(gan);
};

// 获取天干五行
export const getGanWuxingType = (gan) => {
  const wuxingMap = {
    '甲': '木', '乙': '木',
    '丙': '火', '丁': '火',
    '戊': '土', '己': '土',
    '庚': '金', '辛': '金',
    '壬': '水', '癸': '水'
  };
  return wuxingMap[gan] || '';
};

// 判断五行生克关系
export const getWuxingRelation = (myWuxing, otherWuxing) => {
  // 生我者为印，我生者为食伤，克我者为官，我克者为财，同我者为比劫
  if (myWuxing === otherWuxing) return '比劫';

  const shengRelation = {
    '木': '水',  // 水生木
    '火': '木',  // 木生火
    '土': '火',  // 火生土
    '金': '土',  // 土生金
    '水': '金'   // 金生水
  };

  const keRelation = {
    '木': '土',  // 木克土
    '火': '金',  // 火克金
    '土': '水',  // 土克水
    '金': '木',  // 金克木
    '水': '火'   // 水克火
  };

  if (shengRelation[myWuxing] === otherWuxing) return '印';
  if (shengRelation[otherWuxing] === myWuxing) return '食伤';
  if (keRelation[otherWuxing] === myWuxing) return '官';
  if (keRelation[myWuxing] === otherWuxing) return '财';

  return '';
};

// 计算十神
// isDayGan: 是否是日柱天干本身
export const getShishen = (riGan, otherGan, isDayGan = false) => {
  if (!riGan || !otherGan) return '';

  // 只有日柱天干本身才显示为"日主"
  if (isDayGan) {
    return '日主';
  }

  const riWuxing = getGanWuxingType(riGan);
  const otherWuxing = getGanWuxingType(otherGan);
  const relation = getWuxingRelation(riWuxing, otherWuxing);

  const riYang = isYangGan(riGan);
  const otherYang = isYangGan(otherGan);
  const sameYinYang = riYang === otherYang;

  // 根据关系和阴阳判断具体十神
  switch(relation) {
    case '比劫':
      return sameYinYang ? '比肩' : '劫财';
    case '印':
      return sameYinYang ? '偏印' : '正印';
    case '食伤':
      return sameYinYang ? '食神' : '伤官';
    case '官':
      return sameYinYang ? '七杀' : '正官';
    case '财':
      return sameYinYang ? '偏财' : '正财';
    default:
      return '';
  }
};

// 旧版：获取十神颜色（固定映射，保留作兜底；正常走 getShishenColorBySource）
export const getShishenColor = (shishen) => {
  const colorMap = {
    '日主': 'var(--ss-huo-zheng)',
    '比肩': 'var(--wx-tu)',
    '劫财': 'var(--ss-tu-pian)',
    '食神': 'var(--wx-mu)',
    '伤官': 'var(--ss-mu-pian)',
    '偏财': 'var(--wx-jin)',
    '正财': 'var(--ss-jin-zheng)',
    '七杀': 'var(--ss-huo-zheng)',
    '正官': 'var(--wx-huo)',
    '偏印': 'var(--ss-shui-pian)',
    '正印': 'var(--wx-shui)'
  };
  return colorMap[shishen] || 'var(--text-secondary)';
};

// 移动端：十神单字缩写映射
export const abbrShishen = (shishen) => {
  const map = {
    '日主': '主',
    '比肩': '比',
    '劫财': '劫',
    '食神': '食',
    '伤官': '伤',
    '偏财': '财',
    '正财': '才',
    '七杀': '杀',
    '正官': '官',
    '偏印': '枭',
    '正印': '印'
  };
  return map[shishen] || shishen;
};

// 判断“正/偏”
const isZheng = (ss) => ss === '日主' || ss === '日元' || ss === '比肩' || ss === '食神' || (ss && ss.startsWith('正'));

// 新版：按来源天干五行 + 正/偏明暗取十神颜色。
// 正深/偏浅两档已按原 shadeColor 公式（深15%/浅18%）预计算进 tokens.css 的 --ss-*，
// 这里只返回 CSS 变量引用，随主题自动切换。
export const getShishenColorBySource = (shishen, sourceGan) => {
  if (!shishen || !sourceGan) return 'var(--text-secondary)';
  const element = getGanWuxingType(sourceGan);
  const key = { '金': 'jin', '木': 'mu', '水': 'shui', '火': 'huo', '土': 'tu' }[element];
  if (!key) return 'var(--text-secondary)';
  return `var(--ss-${key}-${isZheng(shishen) ? 'zheng' : 'pian'})`;
};